// The inspector's facts grid (spec §15: Volume, Area, …, Data) from a pack's own `qc.json`, which its build
// writes for every mesh and tract it ships. What each structure gets, by what its mesh is:
// - a closed structure (a nucleus, a ventricle, the cerebellum): the volume its mesh encloses and its area;
// - a cortex parcel: its volume, and its pial area on the HD cortex the scene draws (that pack's `qc.json`
//   `parcels`, #618). Its mesh closes the cortical ribbon (pial side, white side and the walls between),
//   so the mesh's own area is about twice the cortical sheet's, and isn't its area;
// - a vessel: the length and median radius of its centreline;
// - a tract: how many of its source streamlines are drawn, and what it connects (`related`).

import type { Catalogue, Structure } from './catalogue.ts';

export interface MeshQc {
  watertight?: boolean;
  volume_mm3?: number;
  area_mm2?: number;
  /** The label's own voxels, for meshes made from a label map. */
  voxel_volume_mm3?: number;
  length_mm?: number;
  radius_median_mm?: number;
  radius_min_mm?: number;
  radius_max_mm?: number;
}

export interface TractQc {
  source_streamlines?: number;
  /** Streamlines drawn. */
  count?: number;
}

/** A catalogue's qc, merged over its chain as its structures are: bases first, each id once. */
export interface CatalogueQc {
  meshes: Map<string, MeshQc & { pack: string }>;
  tracts: Map<string, TractQc & { pack: string }>;
}

const loaded = new Map<string, Promise<CatalogueQc>>();
const areas = new Map<string, Promise<Map<string, ParcelArea>>>();

/** What a pack's `qc.json` holds, as far as the inspector reads it. */
interface QcFile {
  meshes?: Record<string, MeshQc>;
  tracts?: Record<string, TractQc>;
  parcels?: Record<string, { pial_area_mm2?: unknown; vertices?: unknown }>;
}

const files = new Map<string, Promise<QcFile | null>>();

/** A pack's `qc.json`, read once a page: loadQc and loadParcelAreas both want an HD cortex's, and most packs have none,
 *  so each 404 is asked for once (the index doesn't say which packs have one). Null when there's none. */
function qcFile(pack: string, assets: string, fetchFn: typeof fetch): Promise<QcFile | null> {
  const key = `${assets}|${pack}`;
  let p = files.get(key);
  if (!p) {
    p = fetchFn(`${assets}packs/${pack}/qc.json`)
      .then((res) => (res.ok ? (res.json() as Promise<QcFile>) : null))
      .catch(() => null);
    files.set(key, p);
  }
  return p;
}

/** Forget loaded qc (tests). */
export function clearQc() {
  loaded.clear();
  areas.clear();
  files.clear();
}

/** The qc of a catalogue's packs. A pack without a `qc.json` adds nothing; a connectome pack has none. */
export function loadQc(cat: Catalogue, assets = '/', fetchFn: typeof fetch = fetch): Promise<CatalogueQc> {
  const key = `${assets}|${cat.pack.id}@${cat.pack.version}`;
  let p = loaded.get(key);
  if (!p) {
    p = (async () => {
      const out: CatalogueQc = { meshes: new Map(), tracts: new Map() };
      if (cat.kind !== 'anatomy') return out;
      // `chain` is the pack first, then what it extends; structures take the bases' records first.
      for (const pack of [...(cat.chain ?? [cat.pack.id])].reverse()) {
        const qc = await qcFile(pack, assets, fetchFn);
        for (const [id, m] of Object.entries(qc?.meshes ?? {})) if (!out.meshes.has(id)) out.meshes.set(id, { ...m, pack });
        for (const [id, t] of Object.entries(qc?.tracts ?? {})) if (!out.tracts.has(id)) out.tracts.set(id, { ...t, pack });
      }
      return out;
    })();
    loaded.set(key, p);
  }
  return p;
}

/** A DKT parcel's pial area on an HD cortex, and the vertices it covers (its pack's `qc.json` `parcels`). */
export interface ParcelArea {
  pial_area_mm2: number;
  vertices?: number;
  pack: string;
}

/** An HD cortex pack's parcel areas, read once a pack version; none where its qc has no `parcels` (before #618). */
export function loadParcelAreas(pack: string, version?: string, assets = '/', fetchFn: typeof fetch = fetch): Promise<Map<string, ParcelArea>> {
  const key = `${assets}|${pack}@${version ?? ''}`;
  let p = areas.get(key);
  if (!p) {
    p = (async () => {
      const out = new Map<string, ParcelArea>();
      const qc = await qcFile(pack, assets, fetchFn);
      for (const [id, a] of Object.entries(qc?.parcels ?? {})) {
        if (typeof a?.pial_area_mm2 === 'number' && Number.isFinite(a.pial_area_mm2)) out.set(id, { pial_area_mm2: a.pial_area_mm2, ...(typeof a.vertices === 'number' ? { vertices: a.vertices } : {}), pack });
      }
      return out;
    })();
    areas.set(key, p);
  }
  return p;
}

/** On the fs_LR template's cortex the medial wall is unlabelled, so the parcels along it come out small (#618: 9–33 %). */
const MEDIAL_WALL_SHORT = /cingulate|medial-orbitofrontal/;

/** A measure to three significant figures, thousands grouped by a narrow space, as design writes them: 21 400. */
export function formatMeasure(v: number): string {
  if (!Number.isFinite(v)) return '–';
  if (v === 0) return '0';
  const digits = Math.floor(Math.log10(Math.abs(v))) + 1;
  const rounded = digits > 3 ? Math.round(v / 10 ** (digits - 3)) * 10 ** (digits - 3) : v;
  const [int, frac] = rounded.toFixed(Math.max(0, 3 - digits)).split('.');
  const grouped = int!.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return frac ? `${grouped}.${frac}` : grouped;
}

/** A count in full, thousands grouped as measures are: 34 156. */
export const formatCount = (n: number): string => (Number.isFinite(n) ? Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '\u202F') : '–');

/** Label names that are a tool's, not anatomy's: FreeSurfer's `ctx-lh-…`, TractSeg's `C_FP-L` and `AF-L`. */
const MACHINE = /^ctx-[lr]h-|_|-[LR]$/;
const bare = (x: string) =>
  x
    .replace(/\s*\((left|right)\)$/i, '')
    .replace(/^(left|right)\s+/i, '')
    .trim()
    .toLowerCase();

/** A structure's other names worth reading: not a tool's label, not its own name again, up to three. */
export function synonymsOf(s: Pick<Structure, 'name' | 'synonyms'>): string[] {
  const name = bare(s.name);
  const out: string[] = [];
  for (const y of s.synonyms ?? []) {
    if (MACHINE.test(y)) continue;
    const plain = y.replace(/\s*\((left|right)\)$/i, '').trim();
    if (bare(plain) === name || out.some((o) => o.toLowerCase() === plain.toLowerCase())) continue;
    out.push(plain);
    if (out.length === 3) break;
  }
  return out;
}

export interface Fact {
  label: string;
  value: string;
}

const VESSELS = new Set(['artery', 'vein', 'sinus']);

/**
 * A structure's facts from its pack's qc, and how they were measured (for "How this was made"). Measures
 * come only from watertight meshes, so a volume is one the mesh encloses.
 */
export function factsOf(
  s: Structure,
  qc: CatalogueQc | null,
  cat: Pick<Catalogue, 'byId'>,
  cortex?: { areas: ReadonlyMap<string, ParcelArea>; label: string } | null,
): { rows: Fact[]; how: string | null } {
  const rows: Fact[] = [];
  let how: string | null = null;
  const m = qc?.meshes.get(s.id);
  const t = qc?.tracts.get(s.id);
  if (m && VESSELS.has(s.system) && m.length_mm !== undefined) {
    rows.push({ label: 'Length', value: `${formatMeasure(m.length_mm)} mm` });
    if (m.radius_median_mm !== undefined) rows.push({ label: 'Radius', value: `${formatMeasure(m.radius_median_mm)} mm` });
    const range = m.radius_min_mm !== undefined && m.radius_max_mm !== undefined ? `, ${formatMeasure(m.radius_min_mm)} to ${formatMeasure(m.radius_max_mm)} mm along it` : '';
    how = `Length and median radius are its centreline's (${m.pack} qc)${range}.`;
  } else if (m?.watertight && m.volume_mm3 !== undefined) {
    rows.push({ label: 'Volume', value: `${formatMeasure(m.volume_mm3)} mm³` });
    const voxels = m.voxel_volume_mm3 !== undefined ? `, against ${formatMeasure(m.voxel_volume_mm3)} mm³ of its label's voxels` : '';
    if (s.system === 'cortex') how = `Volume is what its mesh encloses (${m.pack} qc)${voxels}. The mesh closes the cortical ribbon, so its own surface isn't the cortex's area.`;
    else {
      if (m.area_mm2 !== undefined) rows.push({ label: 'Area', value: `${formatMeasure(m.area_mm2)} mm²` });
      how = `Volume and area are its mesh's (${m.pack} qc)${voxels}.`;
    }
  }
  // A cortex parcel's area is its pial surface's on the cortex the scene draws, after its volume (spec §15's order).
  const a = s.system === 'cortex' ? cortex?.areas.get(s.id) : undefined;
  if (a) {
    rows.push({ label: 'Area', value: `${formatMeasure(a.pial_area_mm2)} mm²` });
    const short = a.pack === 'mni152-2009c-cortex-hd' && MEDIAL_WALL_SHORT.test(s.id) ? ' The template’s medial wall is unlabelled, so a parcel along it reads small.' : '';
    const over = a.vertices !== undefined ? `, over ${formatCount(a.vertices)} vertices` : '';
    how = `${how ? `${how} ` : ''}Area is its pial surface on ${cortex!.label}, the cortex drawn (${a.pack} qc${over}).${short}`;
  }
  if (t?.count !== undefined) {
    rows.push({ label: 'Drawn', value: `${formatCount(t.count)}${t.source_streamlines !== undefined ? ` of ${formatCount(t.source_streamlines)}` : ''} streamlines` });
    how = `${t.count.toLocaleString('en-US')} of its ${t.source_streamlines?.toLocaleString('en-US') ?? '?'} source streamlines are drawn (${t.pack} qc).`;
  }
  const ends = (role: string) => (s.related ?? []).filter((r) => r.role === role).map((r) => cat.byId.get(r.id)?.name ?? r.id);
  const from = ends('origin');
  const to = ends('target');
  if (from.length && to.length) rows.push({ label: 'Connects', value: `${from.join(', ')} to ${to.join(', ')}` });
  const syn = synonymsOf(s);
  if (syn.length) rows.push({ label: 'Synonyms', value: syn.join(', ') });
  return { rows, how };
}
